import Table, { TableControls, buildTagColumn } from "@/components/Table";
import { sysColors, extendedColors } from "@/assets/theme/colors";
import MetricGrid from "@/components/MetricGrid";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import PieChartComponent from "@/components/Chart/PieChart";
import LineChartComponent from "@/components/Chart/LineChart";

import useTableRowMenu from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { GET_COMPENSATION_PROFILES_QUERY } from "../queries";
import { useQuery } from "@apollo/client";
import { type Path, useParams, useNavigate } from "@/router";
import { DOMAIN, PERMISSION, useHasCapabilityBool, useRequireCapability } from "@/hooks/useCapabilities";
import { useEffect, useState } from "react";
import { formatDate } from "@/pages/_layouts/jobs/_utils";
import { formatDisplayCurrency } from "@/lib/currencyHelpers";
import EmptyState from "@/components/EmptyState";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import Select from "@/components/Select";
import { buildCompensationProfileMetrics } from "./[compProfileId]/compensationProfileMetrics";

export interface CompensationProfile {
  id: string;
  updatedAt: string;
  lastModified: string;
  lastModifiedVia: string;
  createdVia: string;
  baseCompensation: string;
  totalCompensation: string;
  isCurrent: boolean;
  memberClassificationAtTimeOfRecord?: string;
}

const columns: ColumnDef<CompensationProfile>[] = [
  {
    header: "Last Modified",
    accessorKey: "lastModifiedDate",
    enableSorting: true,
    cell: ({ row }) => row.original.lastModified
  },
  {
    header: "Last Modified By",
    accessorKey: "lastModifiedVia",
    meta: { headerClassName: "show-header-in-column" }
  },
  {
    header: "Created By",
    accessorKey: "createdVia",
    meta: { headerClassName: "show-header-in-column" }
  },
  {
    header: "Employed As",
    accessorKey: "memberClassificationAtTimeOfRecord"
  },
  {
    header: "Facility",
    accessorKey: "employerAtTimeOfRecord"
  },
  {
    header: "Base Compensation",
    accessorKey: "baseCompensation"
  },
  {
    header: "Total Compensation",
    accessorKey: "totalCompensation"
  },
  buildTagColumn<CompensationProfile>({
    accessorKey: "status",
    header: "Status",
    getTagLabel: (row) => (row.isCurrent ? "Active" : "Inactive"),
    getTagType: (row) => (row.isCurrent ? "success" : "surface")
  })
];

export default function CompensationProfiles() {
  useRequireCapability({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewCompensationProfiles
  });
  const canManageCompensationProfiles = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.manageCompensationProfiles
  });

  const { ActionCell, Menu: ActionMenu } = useTableRowMenu();
  const { pgaId } = useParams("/people/:pgaId/career");
  const navigate = useNavigate();
  const [pieData, setPieData] = useState<any[]>([]);
  const [lineData, setLineData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<CompensationProfile[]>([]);
  const [sorting, setSorting] = useState<SortingState>([{ id: "lastModifiedDate", desc: true }]);
  const [showHistoricalSnapshotDialog, setShowHistoricalSnapshotDialog] = useState(false);
  const [earlierProfileId, setEarlierProfileId] = useState("");

  const { data, loading } = useQuery(GET_COMPENSATION_PROFILES_QUERY, {
    variables: { pgaId: pgaId }
  });
  const currentProfile = data?.person?.compensationProfiles?.find((profile: any) => !profile.isSnapshot);
  const compensationMetrics: MetricCardType<Path>[] = currentProfile
    ? buildCompensationProfileMetrics(currentProfile, loading, { includeStatus: false })
    : [];

  function formatCurrency(amount: number) {
    return `$${formatDisplayCurrency(amount)}`;
  }

  useEffect(() => {
    if (data?.person?.compensationProfiles) {
      const profiles = data.person.compensationProfiles;

      const currentProfile = profiles.find((profile: any) => !profile.isSnapshot);
      const currentProfileId = currentProfile?.id;

      if (currentProfile) {
        const pieData = [
          { name: "Base Compensation", value: currentProfile.basePay || 0 },
          {
            name: "Bonus Compensation",
            value: currentProfile.bonusIncome || 0
          },
          {
            name: "Lesson Compensation",
            value: currentProfile.lessonIncome || 0
          },
          {
            name: "Other Compensation",
            value: currentProfile.otherIncome || 0
          }
        ];
        setPieData(pieData);
      }

      // Build line chart data by year
      const yearMap = new Map();
      profiles.forEach((profile: any) => {
        const year = new Date(profile.updatedAt).getFullYear();
        const existing = yearMap.get(year) || {
          name: String(year),
          "Annual Total Compensation": 0,
          "Annual Base Compensation": 0,
          count: 0
        };

        existing["Annual Total Compensation"] += profile.totalCompensation || 0;
        existing["Annual Base Compensation"] += profile.basePay || 0;
        existing.count += 1;

        yearMap.set(year, existing);
      });

      // Convert to array and average the values
      const lineData = Array.from(yearMap.values())
        .map((item: any) => ({
          name: item.name,
          "Annual Total Compensation": Math.round(item["Annual Total Compensation"] / item.count),
          "Annual Base Compensation": Math.round(item["Annual Base Compensation"] / item.count)
        }))
        .sort((a: any, b: any) => Number(a.name) - Number(b.name));

      setLineData(lineData);

      const tableData = [...profiles].map((profile: any) => ({
        id: profile.id,
        updatedAt: profile.updatedAt,
        lastModified: formatDate(profile.updatedAt),
        lastModifiedDate: profile.updatedAt,
        lastModifiedVia: profile.lastModificationSource || "Unknown",
        createdVia: profile.creationSource || "Unknown",
        baseCompensation: formatCurrency(profile.basePay || 0),
        totalCompensation: formatCurrency(profile.totalCompensation || 0),
        isCurrent: profile.id === currentProfileId,
        memberClassificationAtTimeOfRecord: profile.memberClassificationAtTimeOfRecord,
        employerAtTimeOfRecord: profile.employerAtTimeOfRecord?.name
      }));

      setTableData(tableData);
    }
  }, [data]);

  const columnsWithActions = [
    ...columns,
    {
      id: "actions",
      header: "",
      cell: ({ row }: any) => {
        const actions: Action[] = [
          {
            text: "View Profile",
            onClick: () => navigate(`/people/${pgaId}/career/compensation/${row.original.id}/view` as any)
          }
        ];

        if (canManageCompensationProfiles) {
          actions.push({
            text: "Edit Profile",
            onClick: () => navigate(`/people/${pgaId}/career/compensation/${row.original.id}/edit` as any)
          });
        }

        return (
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <ActionCell id={row.id} actions={actions} />
          </div>
        );
      },
      size: 50,
      meta: { headerClassName: "text-right" }
    }
  ];

  const handleRowClick = (row: any) => {
    navigate(("/people/" + pgaId + "/career/compensation/" + row.original.id + "/view") as any);
  };

  // Handle sorting of profiles chronologically for comparison when creating historical snapshot.
  const sortedProfiles = [...tableData].sort(
    (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
  );

  const selectableHistoricalProfiles = sortedProfiles.filter((profile) => !profile.isCurrent).reverse();
  const profileOptions = selectableHistoricalProfiles.map((profile) => ({
    label: `${profile.lastModified} - ${profile.memberClassificationAtTimeOfRecord || "Unknown"} - ${
      profile.totalCompensation
    }`,
    value: profile.id
  }));

  const selectedEarlierProfile = sortedProfiles.find((profile) => profile.id === earlierProfileId);
  const selectedEarlierProfileIndex = sortedProfiles.findIndex((profile) => profile.id === earlierProfileId);
  const selectedLaterProfile =
    selectedEarlierProfileIndex >= 0 ? sortedProfiles[selectedEarlierProfileIndex + 1] : null;

  function handleOpenHistoricalSnapshotDialog() {
    setEarlierProfileId("");
    setShowHistoricalSnapshotDialog(true);
  }

  function handleCreateHistoricalSnapshot() {
    if (!selectedEarlierProfile || !selectedLaterProfile) return;

    const searchParams = new URLSearchParams({
      historical: "true",
      from: selectedEarlierProfile.id,
      to: selectedLaterProfile.id
    });

    navigate(`/people/${pgaId}/career/compensation/create?${searchParams.toString()}` as any);
  }

  const hasMetrics = compensationMetrics.length > 0;
  const hasLeadingContent = hasMetrics || lineData.length > 0 || pieData.length > 0;
  const addCompensationActions =
    canManageCompensationProfiles && tableData.length > 1 && selectableHistoricalProfiles.length > 0
      ? [
          {
            text: "Add Compensation Profile",
            iconName: "add" as const,
            onClick: handleOpenHistoricalSnapshotDialog,
            testId: "add-compensation-profile"
          }
        ]
      : [];

  return (
    <>
      {hasLeadingContent && (
        <div className={!hasMetrics ? "pt-12" : undefined}>
          <div className="flex flex-col gap-6">
            {hasMetrics && <MetricGrid metrics={compensationMetrics} layout="expanded" />}
            {(lineData.length > 0 || pieData.length > 0) && (
              <div className="flex flex-col md:flex-row gap-4 md:gap-12">
                {lineData.length > 0 && (
                  <div className="w-full md:w-[66%]">
                    <LineChartComponent
                      data={lineData}
                      lines={[
                        {
                          dataKey: "Annual Total Compensation",
                          name: "Annual Total Compensation",
                          color: extendedColors["extended-success-brand-color"]
                        },
                        {
                          dataKey: "Annual Base Compensation",
                          name: "Annual Base Compensation",
                          color: sysColors["sys-brand-primary"]
                        }
                      ]}
                      isCurrency={true}
                      title="Compensation Over Time"
                      subtitle="Total and Base Compensation History by Year (In Thousands)"
                      loading={loading}
                    />
                  </div>
                )}
                {pieData.length > 0 && (
                  <div className="w-full md:w-[33%]">
                    <PieChartComponent
                      data={pieData}
                      title="Current Total Compensation by Source"
                      totalValue={pieData.reduce((acc: any, item: any) => acc + item.value, 0)}
                      loading={loading}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="pt-12" />
      <Table
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Compensation Profiles"
            caption="This person does not have any compensation profile history."
            iconName="attach_money"
            action={
              canManageCompensationProfiles
                ? {
                    children: "Create Compensation Profile",
                    onClick: () => {
                      navigate(`/people/${pgaId}/career/compensation/create` as any);
                    },
                    icon: "add"
                  }
                : undefined
            }
          />
        )}
        data={tableData}
        columns={columnsWithActions}
        loading={loading}
        sortingState={sorting}
        onSortingChange={setSorting}
        onClickRow={handleRowClick}
        renderControls={
          addCompensationActions.length > 0
            ? (_table, { onSortClick } = {}) => (
                <TableControls actions={addCompensationActions} onSortClick={onSortClick} />
              )
            : undefined
        }
      />
      <ActionMenu />
      {showHistoricalSnapshotDialog && (
        <Dialog
          open={showHistoricalSnapshotDialog}
          onClosed={() => setShowHistoricalSnapshotDialog(false)}
          headline="Add Compensation Profile"
          className="sm:max-w-[560px] min-h-[300px] p-6 pr-2 overflow-visible"
          displayOverflow
          actions={
            <>
              <Button variant="text" onClick={() => setShowHistoricalSnapshotDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="filled"
                onClick={handleCreateHistoricalSnapshot}
                disabled={!selectedEarlierProfile || !selectedLaterProfile}
              >
                Continue
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-2">
            <p>
              You are creating a historical compensation profile. Note that this should <strong>not</strong> be the
              person's new current profile.
            </p>
            <p className="mb-2">
              Please select the existing profile that was in place just before this new compensation started. This
              ensures your records stay in the correct order.
            </p>
            <Select
              label="Previous Compensation Profile"
              value={earlierProfileId}
              onSelect={setEarlierProfileId}
              options={profileOptions}
              className="z-30"
              dropdownHeight="default"
              supportingText={
                selectedEarlierProfile && selectedLaterProfile
                  ? `The historical compensation profile date will be between ${selectedEarlierProfile.lastModified} and ${selectedLaterProfile.lastModified}.`
                  : undefined
              }
              errorText="Select a profile that has a later chronological profile."
              error={!!selectedEarlierProfile && !selectedLaterProfile}
            />
          </div>
        </Dialog>
      )}
    </>
  );
}
